(() => {
  const initialize = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const currency = root.dataset.currency || 'USD';
    const locale = root.dataset.locale || 'en-US';
    const money = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    const formatMoney = (cents) => money.format((Number(cents) || 0) / 100);
    const mainImage = root.querySelector('[data-gallery-main]');
    const cleanup = [];

    root.querySelectorAll('[data-gallery-target]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!mainImage) return;
        mainImage.removeAttribute('srcset');
        mainImage.src = button.dataset.galleryTarget;
        mainImage.alt = button.dataset.galleryAlt || mainImage.alt;
        root.querySelectorAll('[data-gallery-target]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      });
    });

    const setVariant = (source) => {
      const id = source.dataset.variantId;
      const title = source.dataset.variantTitle || '';
      const price = Number(source.dataset.price || 0);
      const compare = Number(source.dataset.compare || 0);
      const available = source.dataset.available === 'true';
      const saving = compare > price ? Math.floor(((compare - price) / compare) * 100) : 0;

      root.querySelectorAll('[data-variant-choice]').forEach((button) => {
        const selected = button.dataset.variantId === id;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      root.querySelectorAll('[data-variant-input]').forEach((input) => {
        input.value = id;
      });
      root.querySelectorAll('[data-selected-variant]').forEach((label) => {
        label.textContent = title;
      });
      root.querySelectorAll('[data-current-price], [data-button-price]').forEach((label) => {
        label.textContent = formatMoney(price);
      });
      root.querySelectorAll('[data-compare-price]').forEach((label) => {
        label.textContent = compare > price ? formatMoney(compare) : '';
        label.classList.toggle('is-hidden', compare <= price);
      });
      root.querySelectorAll('[data-saving-badge]').forEach((label) => {
        label.textContent = saving > 0 ? `Save ${saving}%` : '';
        label.classList.toggle('is-hidden', saving <= 0);
      });
      root.querySelectorAll('[data-add-button], [data-sticky-submit]').forEach((button) => {
        button.disabled = !available;
      });
      root.querySelectorAll('[data-add-label]').forEach((label) => {
        label.textContent = available ? 'Add the 13-piece set' : 'Sold out';
      });

      if (mainImage && source.dataset.variantImage) {
        mainImage.removeAttribute('srcset');
        mainImage.src = source.dataset.variantImage;
        mainImage.alt = `${title} colorway of the complete 13-piece hair set`;
        root.querySelectorAll('[data-gallery-target]').forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    };

    root.querySelectorAll('[data-variant-choice]').forEach((button) => {
      button.addEventListener('click', () => setVariant(button));
    });

    const motionShell = root.querySelector('[data-motion-shell]');
    const motionVideo = motionShell?.querySelector('[data-motion-video]');
    const motionSource = motionVideo?.querySelector('source[data-src]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (motionShell && motionVideo && motionSource) {
      let motionLoaded = false;
      let motionIsNear = false;

      const useStaticFallback = (isError = false) => {
        motionVideo.pause();
        motionShell.classList.toggle('is-motion-error', isError);
        motionShell.classList.toggle('is-static', !isError);
        motionShell.dataset.motionStatus = isError ? 'error-fallback' : 'reduced-motion-fallback';
      };

      const loadAndPlayMotion = () => {
        if (reducedMotion.matches) {
          useStaticFallback(false);
          return;
        }

        motionShell.classList.remove('is-static', 'is-motion-error');
        motionShell.dataset.motionStatus = 'loading';
        if (!motionLoaded) {
          motionSource.src = motionSource.dataset.src;
          motionLoaded = true;
          motionVideo.load();
        }

        const playResult = motionVideo.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => useStaticFallback(true));
        }
      };

      const onMotionPlaying = () => {
        motionShell.dataset.motionStatus = 'playing';
      };

      const onMotionError = () => useStaticFallback(true);
      motionVideo.addEventListener('error', onMotionError);
      motionVideo.addEventListener('playing', onMotionPlaying);
      motionSource.addEventListener('error', onMotionError);

      const motionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          motionIsNear = entry.isIntersecting;
          if (motionIsNear) loadAndPlayMotion();
          else motionVideo.pause();
        });
      }, { rootMargin: '180px 0px', threshold: 0.05 });

      motionObserver.observe(motionShell);

      const onMotionPreference = () => {
        if (reducedMotion.matches) useStaticFallback(false);
        else if (motionIsNear) loadAndPlayMotion();
      };
      reducedMotion.addEventListener('change', onMotionPreference);
      if (reducedMotion.matches) useStaticFallback(false);

      cleanup.push(() => {
        motionObserver.disconnect();
        reducedMotion.removeEventListener('change', onMotionPreference);
        motionVideo.removeEventListener('error', onMotionError);
        motionVideo.removeEventListener('playing', onMotionPlaying);
        motionSource.removeEventListener('error', onMotionError);
        motionVideo.pause();
      });
    }

    const sticky = root.querySelector('[data-v3-sticky-cta]');
    const purchaseBoxes = root.querySelectorAll('[data-v3-purchase]');
    const heroBuyBox = purchaseBoxes[0];
    const offerBuyBox = purchaseBoxes[1];
    const mobileViewport = window.matchMedia('(max-width: 899px)');

    if (sticky && heroBuyBox && offerBuyBox) {
      let stickyFrame = 0;

      const updateSticky = () => {
        stickyFrame = 0;
        const heroRect = heroBuyBox.getBoundingClientRect();
        const offerRect = offerBuyBox.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const betweenPurchaseAreas = heroRect.bottom < -8 && offerRect.top > window.innerHeight * 0.72;
        const pageIsRelevant = rootRect.top < 0 && rootRect.bottom > window.innerHeight * 0.35;
        const visible = mobileViewport.matches && betweenPurchaseAreas && pageIsRelevant;

        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      const scheduleStickyUpdate = () => {
        if (stickyFrame) return;
        stickyFrame = window.requestAnimationFrame(updateSticky);
      };

      window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });
      window.addEventListener('resize', scheduleStickyUpdate, { passive: true });
      mobileViewport.addEventListener('change', scheduleStickyUpdate);
      const purchaseObserver = new IntersectionObserver(scheduleStickyUpdate, {
        threshold: [0, 0.01, 1]
      });
      purchaseBoxes.forEach((box) => purchaseObserver.observe(box));
      scheduleStickyUpdate();

      cleanup.push(() => {
        if (stickyFrame) window.cancelAnimationFrame(stickyFrame);
        window.removeEventListener('scroll', scheduleStickyUpdate);
        window.removeEventListener('resize', scheduleStickyUpdate);
        mobileViewport.removeEventListener('change', scheduleStickyUpdate);
        purchaseObserver.disconnect();
      });
    }

    root.p15693197672521Cleanup = () => cleanup.forEach((callback) => callback());
  };

  const initializeAll = (container = document) => {
    container.querySelectorAll('[data-p15693197672521-page]').forEach(initialize);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-p15693197672521-page]').forEach((root) => {
      if (typeof root.p15693197672521Cleanup === 'function') root.p15693197672521Cleanup();
    });
  });
})();
